// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Battle Rhythm Store (Zustand)
// Manages activations, events, and templates across 3 Ignatius tables
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchTable, createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../lib/api-switch';
import { useAuthStore } from './authStore';

// ── Types ────────────────────────────────────────────────────

export interface Activation {
  id: number;
  incidentId: string;
  opHours: string;       // '12' or '24'
  opStart: string;       // ISO datetime string
  org: string;
  templateId: string;
  status: string;        // 'active' | 'closed'
  closedAt: string;
  creator: string;
  datecreated: string;
}

export interface BREvent {
  id: number;
  incidentId: string;
  title: string;
  category: string;      // briefing | report | shift | external | logistics | planning | other
  scheduledTime: string;  // HH:mm
  durationMinutes: string;
  location: string;
  responsible: string;
  notes: string;
  recurring: string;     // 'true' | 'false'
  adHoc: string;         // 'true' | 'false'
  status: string;        // 'upcoming' | 'in-progress' | 'completed' | 'skipped'
  completedBy: string;
  creator: string;
  datecreated: string;
}

export interface BRTemplate {
  id: number;
  name: string;
  description: string;
  opHours: string;
  events: string;        // JSON string of template event array
  creator: string;
  datecreated: string;
}

export interface TemplateEvent {
  title: string;
  category: string;
  scheduledTime: string;
  durationMinutes: number;
  location: string;
  responsible: string;
  notes: string;
  recurring: boolean;
}

interface BattleRhythmStore {
  activations: Activation[];
  events: BREvent[];
  templates: BRTemplate[];
  loading: boolean;
  error: string | null;

  loadActivations: (incidentId: string) => Promise<void>;
  createActivation: (data: Partial<Activation>) => Promise<number>;
  updateActivation: (rid: number, data: Partial<Activation>) => Promise<void>;
  closeActivation: (rid: number) => Promise<void>;

  loadEvents: (incidentId: string) => Promise<void>;
  createEvent: (data: Partial<BREvent>) => Promise<number>;
  updateEvent: (rid: number, data: Partial<BREvent>) => Promise<void>;
  deleteEvent: (rid: number, incidentId: string) => Promise<void>;

  loadTemplates: () => Promise<void>;
  createTemplate: (data: Partial<BRTemplate>) => Promise<number>;
  updateTemplate: (rid: number, data: Partial<BRTemplate>) => Promise<void>;
  deleteTemplate: (rid: number) => Promise<void>;

  getActivationsForIncident: (incidentId: string) => Activation[];
  getEventsForIncident: (incidentId: string) => BREvent[];
}

// Normalize time: handle "0700", "07:00", "2026-03-30T08:00:00", etc. → "HH:mm"
function normalizeTime(t: string): string {
  if (!t) return '';
  const s = t.trim();
  // ISO datetime: "2026-03-30T08:00:00" → extract "08:00"
  if (s.includes('T')) {
    const match = s.match(/T(\d{1,2}):(\d{2})/);
    if (match) return `${match[1].padStart(2, '0')}:${match[2]}`;
  }
  // Military time without colon: "0700" → "07:00", "900" → "09:00"
  if (/^\d{3,4}$/.test(s)) {
    const padded = s.padStart(4, '0');
    return `${padded.slice(0, 2)}:${padded.slice(2, 4)}`;
  }
  // Already "HH:mm" or "HH:mm:ss" — return first two parts
  const parts = s.split(':');
  if (parts.length >= 2) {
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    if (!isNaN(hh) && !isNaN(mm)) return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
  return s;
}

// ── Store ────────────────────────────────────────────────────

export const useBattleRhythmStore = create<BattleRhythmStore>((set, get) => ({
  activations: [],
  events: [],
  templates: [],
  loading: false,
  error: null,

  // ── Activations ──────────────────────────────────────────

  loadActivations: async (incidentId: string) => {
    set({ loading: true, error: null });
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const raw = await fetchTable(TABLES.brMeta, { token, limit: 5000 });
      const activations: Activation[] = raw
        .filter((row: Record<string, unknown>) => String(row.incidentId || '') === incidentId)
        .map((row: Record<string, unknown>) => ({
          id: Number(row.id) || 0,
          incidentId: String(row.incidentId || ''),
          opHours: String(row.opPeriodHours || row.opHours || '12'),
          opStart: String(row.opPeriodStart || row.opStart || ''),
          org: String(row.orgName || row.org || ''),
          templateId: String(row.templateId || ''),
          status: String(row.brStatus || row.status || 'active'),
          closedAt: String(row.closedAt || ''),
          creator: String(row.creator || ''),
          datecreated: String(row.datecreated || ''),
        }));
      set({ activations, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load activations' });
    }
  },

  createActivation: async (data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FM = FIELDS.brMeta;
    const fields: Array<[string, string]> = [
      [FM.incidentId, data.incidentId || ''],
      [FM.opHours, data.opHours || '12'],
      [FM.status, data.status || 'active'],
      [FM.creator, data.creator || ''],
      [FM.org, data.org || ''],
      [FM.opStart, data.opStart || ''],
      [FM.templateId, data.templateId || ''],
    ];
    const rid = await createRecord(TABLES.brMeta, fields, token);
    if (data.incidentId) await get().loadActivations(data.incidentId);
    return rid;
  },

  updateActivation: async (rid, data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FM = FIELDS.brMeta;
    const fields: Array<[string, string]> = [];
    if (data.opHours !== undefined) fields.push([FM.opHours, data.opHours]);
    if (data.opStart !== undefined) fields.push([FM.opStart, data.opStart]);
    if (data.org !== undefined) fields.push([FM.org, data.org]);
    if (data.status !== undefined) fields.push([FM.status, data.status]);
    if (data.closedAt !== undefined) fields.push([FM.closedAt, data.closedAt]);
    if (fields.length > 0) await updateRecord(TABLES.brMeta, rid, fields, token);
    const existing = get().activations.find(a => a.id === rid);
    if (existing?.incidentId) await get().loadActivations(existing.incidentId);
  },

  closeActivation: async (rid) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FM = FIELDS.brMeta;
    await updateRecord(TABLES.brMeta, rid, [
      [FM.status, 'closed'],
      [FM.closedAt, new Date().toISOString()],
    ], token);
    const existing = get().activations.find(a => a.id === rid);
    if (existing?.incidentId) await get().loadActivations(existing.incidentId);
  },

  // ── Events ───────────────────────────────────────────────

  loadEvents: async (incidentId: string) => {
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const raw = await fetchTable(TABLES.brEvents, { token, limit: 5000 });
      const events: BREvent[] = raw
        .filter((row: Record<string, unknown>) => String(row.incidentId || '') === incidentId)
        .map((row: Record<string, unknown>) => ({
          id: Number(row.id) || 0,
          incidentId: String(row.incidentId || ''),
          title: String(row.title || ''),
          category: String(row.category || 'other'),
          scheduledTime: normalizeTime(String(row.scheduledTime || '')),
          durationMinutes: String(row.durationMinutes || '30'),
          location: String(row.location || ''),
          responsible: String(row.responsible || ''),
          notes: String(row.notes || ''),
          recurring: String(row.recurring || 'false'),
          adHoc: String(row.adHoc || 'false'),
          status: String(row.status || 'upcoming'),
          completedBy: String(row.completedBy || ''),
          creator: String(row.creator || ''),
          datecreated: String(row.datecreated || ''),
        }));
      set({ events });
    } catch (err) {
      console.error('Failed to load BR events:', err);
    }
  },

  createEvent: async (data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FE = FIELDS.brEvents;
    const fields: Array<[string, string]> = [
      [FE.incidentId, data.incidentId || ''],
      [FE.title, data.title || ''],
      [FE.category, data.category || 'other'],
      [FE.scheduledTime, data.scheduledTime || '00:00'],
      [FE.durationMinutes, data.durationMinutes || '30'],
      [FE.location, data.location || ''],
      [FE.responsible, data.responsible || ''],
      [FE.notes, data.notes || ''],
      [FE.recurring, data.recurring || 'false'],
    ];
    // POST max 10, so add remaining in PUT
    const rid = await createRecord(TABLES.brEvents, fields, token);
    // Update with remaining fields
    const extraFields: Array<[string, string]> = [
      [FE.adHoc, data.adHoc || 'false'],
      [FE.status, data.status || 'upcoming'],
      [FE.creator, data.creator || ''],
    ];
    await updateRecord(TABLES.brEvents, rid, extraFields, token);
    if (data.incidentId) await get().loadEvents(data.incidentId);
    return rid;
  },

  updateEvent: async (rid, data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FE = FIELDS.brEvents;
    const fields: Array<[string, string]> = [];
    if (data.title !== undefined) fields.push([FE.title, data.title]);
    if (data.category !== undefined) fields.push([FE.category, data.category]);
    if (data.scheduledTime !== undefined) fields.push([FE.scheduledTime, data.scheduledTime]);
    if (data.durationMinutes !== undefined) fields.push([FE.durationMinutes, data.durationMinutes]);
    if (data.location !== undefined) fields.push([FE.location, data.location]);
    if (data.responsible !== undefined) fields.push([FE.responsible, data.responsible]);
    if (data.notes !== undefined) fields.push([FE.notes, data.notes]);
    if (data.recurring !== undefined) fields.push([FE.recurring, data.recurring]);
    if (data.adHoc !== undefined) fields.push([FE.adHoc, data.adHoc]);
    if (data.status !== undefined) fields.push([FE.status, data.status]);
    if (data.completedBy !== undefined) fields.push([FE.completedBy, data.completedBy]);
    if (fields.length > 0) await updateRecord(TABLES.brEvents, rid, fields, token);
    const existing = get().events.find(e => e.id === rid);
    const incidentId = data.incidentId || existing?.incidentId;
    if (incidentId) await get().loadEvents(incidentId);
  },

  deleteEvent: async (rid, incidentId) => {
    const token = useAuthStore.getState().token ?? undefined;
    await deleteRecord(TABLES.brEvents, rid, token);
    set(state => ({ events: state.events.filter(e => e.id !== rid) }));
    if (incidentId) await get().loadEvents(incidentId);
  },

  // ── Templates ────────────────────────────────────────────

  loadTemplates: async () => {
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const raw = await fetchTable(TABLES.brTemplates, { token, limit: 1000 });
      const templates: BRTemplate[] = raw.map((row: Record<string, unknown>) => ({
        id: Number(row.id) || 0,
        name: String(row.name || ''),
        description: String(row.description || ''),
        opHours: String(row.opHours || '12'),
        events: String(row.events || '[]'),
        creator: String(row.creator || ''),
        datecreated: String(row.datecreated || ''),
      }));
      set({ templates });
    } catch (err) {
      console.error('Failed to load BR templates:', err);
    }
  },

  createTemplate: async (data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FT = FIELDS.brTemplates;
    const fields: Array<[string, string]> = [
      [FT.name, data.name || ''],
      [FT.description, data.description || ''],
      [FT.opHours, data.opHours || '12'],
      [FT.events, data.events || '[]'],
      [FT.creator, data.creator || ''],
    ];
    const rid = await createRecord(TABLES.brTemplates, fields, token);
    await get().loadTemplates();
    return rid;
  },

  updateTemplate: async (rid, data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FT = FIELDS.brTemplates;
    const fields: Array<[string, string]> = [];
    if (data.name !== undefined) fields.push([FT.name, data.name]);
    if (data.description !== undefined) fields.push([FT.description, data.description]);
    if (data.opHours !== undefined) fields.push([FT.opHours, data.opHours]);
    if (data.events !== undefined) fields.push([FT.events, data.events]);
    if (fields.length > 0) await updateRecord(TABLES.brTemplates, rid, fields, token);
    await get().loadTemplates();
  },

  deleteTemplate: async (rid) => {
    const token = useAuthStore.getState().token ?? undefined;
    await deleteRecord(TABLES.brTemplates, rid, token);
    set(state => ({ templates: state.templates.filter(t => t.id !== rid) }));
  },

  // ── Computed ─────────────────────────────────────────────

  getActivationsForIncident: (incidentId: string) =>
    get().activations.filter(a => String(a.incidentId) === String(incidentId)),

  getEventsForIncident: (incidentId: string) =>
    get().events.filter(e => String(e.incidentId) === String(incidentId)),
}));
